import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useState, useEffect } from 'react';
import { useGetSalesReturnsQuery, useDeleteSalesReturnMutation } from '../../../store/api/salesApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

function SalesReturnsList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, error } = useGetSalesReturnsQuery({ 
    limit: 50,
    search: debouncedSearch
  });
  const [deleteSalesReturn] = useDeleteSalesReturnMutation();
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error loading sales returns</Typography>;

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this sales return? This will also reverse the stock changes.')) {
      try {
        await deleteSalesReturn(id).unwrap();
      } catch (err) {
        alert('Failed to delete sales return: ' + (err.data?.message || err.message));
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Sales Returns
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/sales/returns/new')}
          sx={{ borderRadius: 2 }}
        >
          New Sales Return
        </Button>
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by Return #, Customer, or Product Name/Brand..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ bgcolor: 'white' }}
        />
      </Box>

      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Return Number</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Return Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{`Total Amount (${currency})`}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.salesReturns?.length > 0 ? (
                  data.salesReturns.map((ret) => (
                    <TableRow key={ret.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{ret.returnNumber}</TableCell>
                      <TableCell>{ret.customer?.name || '-'}</TableCell>
                      <TableCell>{new Date(ret.returnDate).toLocaleDateString()}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {formatAmount(ret.totalAmount)}
                      </TableCell>
                      <TableCell>
                        {ret.items?.some(it => it.isDamaged) && (
                          <Chip label="Damaged" color="error" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/sales/returns/${ret.id}`)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(ret.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No sales returns found</Typography>
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/sales/returns/new')}
                        sx={{ mt: 2 }}
                      >
                        Create First Sales Return
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}

export default SalesReturnsList;

